/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
import { Grid } from '@material-ui/core'
import { FORM_ERROR } from 'final-form'
import React, { useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import clsx from 'clsx'
import FormSubmitButton from '../../Global/Submit/Button'
import { useStyles } from './Style'
import { Props } from './Type'
import FieldsContainer from './FieldsContainer'
import FormError from './Error'

const FlatsyForm = <T extends {}>({
  formData,
  fields,
  submitEvent,
  invalidSubmitEvent,
  customButton,
  secondaryButton,
  className,
  submitText,
  layout,
  withSubmitDisabled,
  withSubmitDisabledAndCheckUpdate,
  buttonInFieldGrid,
  success,
  errorButton,
  clearOnSubmit,
}: Props<T>): React.ReactElement => {
  /* --------------------------------- STATES --------------------------------- */

  const classes = useStyles()
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [hasError, setHasError] = useState<string | boolean>(false)

  /* ----------------------------- EVENT FUNCTION ----------------------------- */

  /**
   * @description helper to render final form error
   * @param res
   */
  const showErrorAlerte = (res: unknown) => {
    if (res) {
      if (typeof res === 'object') {
        const convertToObject = res as object
        // eslint-disable-next-line no-prototype-builtins
        if (Object(convertToObject).hasOwnProperty(FORM_ERROR)) {
          const resFormated = convertToObject as { [FORM_ERROR]: string }
          setHasError(resFormated[FORM_ERROR])
        }
      }
    }
  }

  /**
   * @description update form callback
   * @param values
   */
  const onUpdate = async (values: T): Promise<undefined | unknown> => {
    setHasError(false)
    if (submitEvent) {
      const showJustSubmittedTimeout = setTimeout(() => setJustSubmitted(true), 50) // Timeout to avoid showing success before form validation errors
      const res = await submitEvent(values)
      if (typeof res !== undefined) {
        clearTimeout(showJustSubmittedTimeout)
        setJustSubmitted(false)
        showErrorAlerte(res)
        return res
      }

      setTimeout(() => setJustSubmitted(false), 2000)

      return undefined
    }
    return false
  }

  const onClick = (): void => {
    setHasError(false)
  }

  const isDisabled = (invalid: boolean, modifiedSinceLastSubmit: boolean): boolean => {
    if (withSubmitDisabled) {
      return invalid
    }
    if (withSubmitDisabledAndCheckUpdate) {
      if (invalid) {
        return !modifiedSinceLastSubmit
      }

      return false
    }

    return false
  }

  const renderButton = (invalid: boolean, modifiedSinceLastSubmit: boolean) =>
    customButton ? (
      customButton(true, isDisabled(invalid, modifiedSinceLastSubmit))
    ) : (
      <>
        <FormSubmitButton
          type="submit"
          className={clsx(
            className ? className.submit : undefined,
            success && classes.success,
            errorButton && classes.errorButton
          )}
          disabled={isDisabled(invalid, modifiedSinceLastSubmit)}
        >
          {submitText || null}
        </FormSubmitButton>
        {secondaryButton && secondaryButton}
      </>
    )

  /* --------------------------------- RENDER --------------------------------- */

  return (
    <FinalForm
      onSubmit={onUpdate}
      initialValues={formData}
      mutators={{
        setValue: ([field, value], state, { changeValue }) => {
          changeValue(state, field, () => value)
        },
      }}
    >
      {({ handleSubmit, invalid, modifiedSinceLastSubmit, values, form }) => {
        const TypeWindow = window as any
        if (!TypeWindow.Flatsy) {
          TypeWindow.Flatsy = {}
        }
        TypeWindow.Flatsy.finalForm = {
          setValue: form.mutators.setValue,
        }
        return (
          <form
            onSubmit={async (event) => {
              if (invalid) {
                invalidSubmitEvent?.()
                setHasError(
                  'Veuillez remplir les champs obligatoires indiqués ci-dessous en rouge afin de pouvoir procéder'
                )
              }

              const submit = await handleSubmit(event)
              if (clearOnSubmit) {
                if (
                  submit &&
                  submit['FINAL_FORM/form-error'] !== true &&
                  withSubmitDisabledAndCheckUpdate
                ) {
                  //
                } else {
                  setHasError(false)
                  form.restart()
                }
              }
            }}
            className={className ? className.form : undefined}
            data-testid="form"
          >
            <FormError hasError={hasError} close={onClick} />
            <Grid container spacing={1}>
              <FieldsContainer
                values={values}
                fields={fields}
                layout={layout}
                justSubmitted={justSubmitted}
              />
              {buttonInFieldGrid && renderButton(invalid, modifiedSinceLastSubmit)}
            </Grid>
            {!buttonInFieldGrid && renderButton(invalid, modifiedSinceLastSubmit)}
          </form>
        )
      }}
    </FinalForm>
  )
}

export default FlatsyForm
